import { useState } from "react";
import { parseScan, type ScanTarget } from "@iltocat/shared";
import { CameraView, hasCamera } from "../components/CameraView.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";

/**
 * Scan a label or a barcode and land on the thing.
 *
 * Two ways in, both ending at `onTarget`: the camera (`CameraView`, which
 * says how it reads), or the box, where a keyboard-style barcode scanner types
 * what it reads and presses Enter, and so can a person.
 */

export function Scan({ onTarget }: { onTarget: (target: ScanTarget) => void }) {
  const [typed, setTyped] = useState("");
  const [camera, setCamera] = useState<"idle" | "on" | "unsupported" | "denied">(
    hasCamera() ? "idle" : "unsupported",
  );
  const [message, setMessage] = useState<string | null>(null);

  function submit(raw: string) {
    const target = parseScan(raw);
    if (!target) {
      setMessage("Nothing to look up yet.");
      return;
    }
    onTarget(target);
  }

  return (
    <>
      <PageHeader title="Scan" meta={<span>A label, or a serial number off a barcode.</span>} />

      <Panel title="Camera">
        {camera === "on" ? (
          <>
            <CameraView onRead={submit} onFail={() => setCamera("denied")} />
            <button type="button" className="button button-quiet" onClick={() => setCamera("idle")}>
              Stop the camera
            </button>
          </>
        ) : camera === "unsupported" ? (
          <Notice tone="warn">
            This browser cannot use the camera here. Type or scan below instead: a barcode scanner that types
            works here as it is.
          </Notice>
        ) : (
          <>
            {camera === "denied" && (
              <Notice tone="error">
                The camera could not start. Check that this site may use it, or type below.
              </Notice>
            )}
            <button type="button" className="button" onClick={() => setCamera("on")}>
              <Icon name="camera" size={18} />
              <span>Start the camera</span>
            </button>
          </>
        )}
      </Panel>

      <Panel
        title="Type or scan a code"
        note="Enter looks it up. A label opens its item or shelf; anything else is searched for."
      >
        <form
          className="inline-add"
          onSubmit={(event) => {
            event.preventDefault();
            submit(typed);
          }}
        >
          <Icon name="qr" size={18} />
          <input
            className="input"
            type="text"
            aria-label="Code or serial number"
            value={typed}
            autoComplete="off"
            onChange={(event) => setTyped(event.target.value)}
          />
          <button className="button" type="submit">
            Look up
          </button>
        </form>
        {message && <Notice tone="warn">{message}</Notice>}
      </Panel>
    </>
  );
}
